import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Strategy } from "@front/m19-api-client";
import { BrandingFilter, ProductGroupEx, SegmentConfigType, SegmentEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  SbStrategyService,
  SdStrategyService,
  SegmentService,
  SpStrategyService,
} from "@front/m19-services";
import { ICheckboxComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { CompactFilterService } from "@m19-board/shared/compact-filter/compact-filter.service";
import { AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, map, switchMap } from "rxjs";

export type TrafficStatFilter<T> = {
  filterValue: T;
  exclude?: boolean;
};

export interface TrafficStatsFilter {
  enableMultiAsin: boolean;
  productSegments: TrafficStatFilter<SegmentEx[]>[];
  keywordSegments: TrafficStatFilter<SegmentEx[]>[];
  advertisedProducts: TrafficStatFilter<string[]>[];
}

export const DefaultTrafficStatsFilter: TrafficStatsFilter = {
  enableMultiAsin: true,
  productSegments: [],
  keywordSegments: [],
  advertisedProducts: [],
};

@UntilDestroy()
@Component({
  selector: "app-traffic-stats-filter",
  templateUrl: "./traffic-stats-filter.component.html",
  styleUrls: ["./traffic-stats-filter.component.scss"],
  providers: [AsinFilterService],
  imports: [TranslocoDirective, CompactFilterComponent, ICheckboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrafficStatsFilterComponent implements OnInit {
  private accountSelection = inject(AccountSelectionService);
  private segmentService = inject(SegmentService);
  private asinService = inject(AsinService);
  private spStrategyService = inject(SpStrategyService);
  private sbStrategyService = inject(SbStrategyService);
  private sdStrategyService = inject(SdStrategyService);

  private readonly compactFilterService = inject(CompactFilterService);
  private readonly asinFilterService = inject(AsinFilterService);

  @Input()
  set initSegment(value: SegmentEx) {
    if (value) {
      const filterType =
        value.segmentType === SegmentConfigType.KeywordSegment
          ? FilterCategory.KeywordSegment
          : FilterCategory.ProductSegment;
      this.initFilterValue.set({
        filterType,
        values: [value],
      });
    } else {
      this.initFilterValue.set(undefined);
    }
  }

  @Output()
  filterChange = new EventEmitter<TrafficStatsFilter>();

  selectedFilters = signal<Filter<TrafficStatsFilterCategory>[]>([]);
  selectedTrafficStatsFilter = signal<TrafficStatsFilter>(DefaultTrafficStatsFilter);
  initFilterValue = signal<
    | {
        filterType: FilterCategory;
        values: SegmentEx[] | ProductGroupEx[] | Strategy[] | BrandingFilter[] | string[];
      }
    | undefined
  >(undefined);

  private customField1Map = new Map<string, string[]>();
  private customField2Map = new Map<string, string[]>();
  strategyAsinMap: Map<number, string[]> = new Map();
  readonly _filters = computed(() => {
    return this.filters().map((f) => {
      if (f.type === this.initFilterValue()?.filterType) {
        f.values = this.initFilterValue()?.values ?? [];
      }
      return f;
    });
  });

  private readonly segments = toSignal(
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      map((segments) => Array.from(segments.values())),
    ),
  );

  private readonly productGroups = toSignal(this.asinFilterService.productGroups$);

  private readonly strategies = toSignal(this.asinFilterService.strategies$);

  private readonly brandingFilters = toSignal(this.asinFilterService.brandTrafficOptions$);

  private readonly productTag1Values = toSignal(this.asinFilterService.productTag1$);
  private readonly productTag2Values = toSignal(this.asinFilterService.productTag2$);

  private keywordSegments = computed(() => {
    return this.segments()?.filter((s) => s.segmentType === SegmentConfigType.KeywordSegment) ?? [];
  });
  private productSegments = computed(() => {
    return this.segments()?.filter((s) => s.segmentType === SegmentConfigType.ProductSegment) ?? [];
  });

  private readonly filters = computed<Filter<TrafficStatsFilterCategory>[]>(() => {
    const selectedFilters = this.selectedFilters();
    return [
      this.compactFilterService.getFilterWithOptions(FilterCategory.KeywordSegment, this.keywordSegments(), {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.KeywordSegment)?.exclude,
        values:
          this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.KeywordSegment)?.values ?? [],
      }),
      this.compactFilterService.getFilterWithOptions(FilterCategory.ProductSegment, this.productSegments(), {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductSegment)?.exclude,
        values:
          this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductSegment)?.values ?? [],
      }),
      this.compactFilterService.getFilterWithOptions(FilterCategory.BrandTraffic, this.brandingFilters() ?? [], {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.BrandTraffic)?.exclude,
        values:
          this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.BrandTraffic)?.values ?? [],
      }),
      this.compactFilterService.getFilterWithOptions(FilterCategory.ProductGroup, this.productGroups() ?? [], {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductGroup)?.exclude,
        values:
          this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductGroup)?.values ?? [],
        excludeModeAllowed: true,
      }),
      this.compactFilterService.getFilterWithOptions(FilterCategory.Strategy, this.strategies() ?? [], {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.Strategy)?.exclude,
        values: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.Strategy)?.values ?? [],
        excludeModeAllowed: true,
      }),
      this.compactFilterService.getFilterWithOptions(FilterCategory.BrandProducts, this.brandingFilters() ?? [], {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.BrandProducts)?.exclude,
        values:
          this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.BrandProducts)?.values ?? [],
        excludeModeAllowed: true,
      }),
      this.compactFilterService.getFilterWithOptions(FilterCategory.ProductTag1, this.productTag1Values() ?? [], {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductTag1)?.exclude,
        values: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductTag1)?.values ?? [],
        excludeModeAllowed: true,
      }),
      this.compactFilterService.getFilterWithOptions(FilterCategory.ProductTag2, this.productTag2Values() ?? [], {
        exclude: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductTag2)?.exclude,
        values: this.compactFilterService.findFilterWithType(selectedFilters, FilterCategory.ProductTag2)?.values ?? [],
        excludeModeAllowed: true,
      }),
    ];
  });

  ngOnInit(): void {
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe(() => {
      // reset the filter on account marketplace change
      if (this.selectedTrafficStatsFilter() !== DefaultTrafficStatsFilter) {
        this.initFilterValue.set(undefined);
        this.selectedTrafficStatsFilter.set(DefaultTrafficStatsFilter);
        this.filterChange.emit(this.selectedTrafficStatsFilter());
      }
    });
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) => this.asinService.getInventoryConfig(am.accountId, am.marketplace)),
      )
      .subscribe((config) => {
        if (config.customField1Name) {
          const filter = this._filters().find((f) => f.type === FilterCategory.ProductTag1)!;
          filter.label = config.customField1Name;
        }
        if (config.customField2Name) {
          const filter = this._filters().find((f) => f.type === FilterCategory.ProductTag2)!;
          filter.label = config.customField2Name;
        }
      });
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
      )
      .subscribe((catalog) => {
        this.customField1Map.clear();
        this.customField2Map.clear();
        for (const product of catalog.products) {
          if (product.productTag1) {
            if (!this.customField1Map.has(product.productTag1)) {
              this.customField1Map.set(product.productTag1, []);
            }
            this.customField1Map.get(product.productTag1)!.push(product.asin!);
          }
          if (product.productTag2) {
            if (!this.customField2Map.has(product.productTag2)) {
              this.customField2Map.set(product.productTag2, []);
            }
            this.customField2Map.get(product.productTag2)!.push(product.asin!);
          }
        }
      });
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am) =>
          combineLatest([
            this.spStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
            this.sbStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
            this.sdStrategyService.getStrategiesPerAsin(am.accountId, am.marketplace),
          ]),
        ),
      )
      .subscribe(([spStrategiesPerAsin, sbStrategiesPerAsin, sdStrategiesPerAsin]) => {
        this.strategyAsinMap.clear();
        for (const [asin, strategies] of [...spStrategiesPerAsin, ...sbStrategiesPerAsin, ...sdStrategiesPerAsin]) {
          for (const strategy of strategies) {
            if (!this.strategyAsinMap.has(strategy.strategyId!)) {
              this.strategyAsinMap.set(strategy.strategyId!, []);
            }
            this.strategyAsinMap.get(strategy.strategyId!)!.push(asin);
          }
        }
      });
  }

  enableMultiAsin() {
    const filter = this.selectedTrafficStatsFilter();
    filter.enableMultiAsin = !filter.enableMultiAsin;
    this.filterChange.emit(filter);
    this.selectedTrafficStatsFilter.set(filter);
  }

  applyFilter(filters: Filter<TrafficStatsFilterCategory>[]) {
    this.selectedFilters.set(filters);

    const keywordSegments: TrafficStatFilter<SegmentEx[]>[] = [];
    const productSegments: TrafficStatFilter<SegmentEx[]>[] = [];
    const advertisedProducts: TrafficStatFilter<string[]>[] = [];
    for (const filter of filters) {
      if (filter.values && filter.values.length === 0) {
        continue;
      }
      if (filter.type === FilterCategory.KeywordSegment) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.KeywordSegment);
        keywordSegments.push({ filterValue: f?.values ?? [], exclude: filter.exclude });
      } else if (filter.type === FilterCategory.ProductSegment) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.ProductSegment);
        productSegments.push({ filterValue: f?.values ?? [], exclude: filter.exclude });
      } else if (filter.type === FilterCategory.ProductGroup) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.ProductGroup);
        const productGroupAsins = new Set(f?.values?.flatMap((pg) => pg.items) ?? []);
        advertisedProducts.push({ filterValue: Array.from(productGroupAsins.values()), exclude: filter.exclude });
      } else if (filter.type === FilterCategory.Strategy) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.Strategy);
        const strategyAsins = new Set(f?.values?.flatMap((s) => this.strategyAsinMap.get(s.strategyId!)!) ?? []);
        advertisedProducts.push({ filterValue: Array.from(strategyAsins.values()), exclude: filter.exclude });
      } else if (filter.type === FilterCategory.BrandProducts) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.BrandProducts);
        const brandAsins = new Set(f?.values?.flatMap((b) => b.asins) ?? []);
        advertisedProducts.push({ filterValue: Array.from(brandAsins.values()), exclude: filter.exclude });
      } else if (filter.type === FilterCategory.BrandTraffic) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.BrandTraffic);
        keywordSegments.push({
          filterValue: f?.values?.map((b) => b.keywordSegment) ?? [],
          exclude: filter.exclude,
        });
        productSegments.push({
          filterValue: f?.values?.map((b) => b.productSegment) ?? [],
          exclude: filter.exclude,
        });
      } else if (filter.type === FilterCategory.ProductTag1) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.ProductTag1);
        const asins = f?.values?.flatMap((v) => this.customField1Map.get(v)!) ?? [];
        advertisedProducts.push({ filterValue: asins, exclude: filter.exclude });
      } else if (filter.type === FilterCategory.ProductTag2) {
        const f = this.compactFilterService.findFilterWithType(filters, FilterCategory.ProductTag2);
        const asins = f?.values?.flatMap((v) => this.customField2Map.get(v)!) ?? [];
        advertisedProducts.push({ filterValue: asins, exclude: filter.exclude });
      }
    }
    let filter = this.selectedTrafficStatsFilter();
    filter = { ...filter, keywordSegments, productSegments, advertisedProducts };
    this.filterChange.emit(filter);
    this.selectedTrafficStatsFilter.set(filter);
  }

  protected readonly DefaultTrafficStatsFilter = DefaultTrafficStatsFilter;
}

type TrafficStatsFilterCategory =
  | FilterCategory.KeywordSegment
  | FilterCategory.ProductSegment
  | FilterCategory.BrandTraffic
  | FilterCategory.ProductGroup
  | FilterCategory.Strategy
  | FilterCategory.BrandProducts
  | FilterCategory.ProductTag1
  | FilterCategory.ProductTag2;
